import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { fetchFindHelpData } from '../services/findHelpService';
import { fetchTaskData } from '../services/taskService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Home = ({ token }) => {
  const [dashboardModel, setDashboardModel] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [taskChartData, setTaskChartData] = useState(null);
  const [taskDateFrom, setTaskDateFrom] = useState('2024-09-17');
  const [taskDateTo, setTaskDateTo] = useState('2025-10-17');
  const [findHelpTableData, setFindHelpTableData] = useState([]);
  const [showFindHelpTable, setShowFindHelpTable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const chartRef = useRef();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5199';

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        // POST API call to /api/FindHelp/referralstatus
        let referralStatusData = null;
        if (token) {
          // Only call referralstatus API on mount
          const referralStatusParams = new URLSearchParams({
            externalUserId: 'PatilSmi',
            MemberIDs: 'QR3M00000070041',
            StartDate: '2024-08-05T00:00:00',
            EndDate: '2025-09-04T00:00:00'
          }).toString();
          const referralStatusResponse = await fetch(`${API_BASE_URL}/api/FindHelp/referralstatus?${referralStatusParams}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!referralStatusResponse.ok) {
            throw new Error('Failed to fetch FindHelp referral status');
          }
          referralStatusData = await referralStatusResponse.json();
          // Extract memberID from response if present
          let memberID = '';
          if (referralStatusData.memberID) {
            memberID = referralStatusData.memberID;
          } else if (referralStatusData.referralStatusList && referralStatusData.referralStatusList.length > 0) {
            memberID = referralStatusData.referralStatusList[0].memberID;
          }
          setDashboardModel({ ...referralStatusData, memberID });
        } else {
          // If no token, set empty data
          setDashboardModel({
            xValues: [],
            lessThan30Days: [],
            between30And45Days: [],
            greaterThan45Days: []
          });
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [token]);

  // Fetch Home Visit Task data (hardcoded params)
  // Track if user has changed the date filter
  const [dateFilterActive, setDateFilterActive] = useState(false);

  // On mount, fetch from /api/Task/status
  useEffect(() => {
    const fetchInitialTaskStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        if (token) {
          const params = new URLSearchParams({
            CaseManagerOrQueueId: 'patilsmi',
            DueDateFrom: '2024-09-17T00:00:00',
            DueDateTo: '2025-10-17T00:00:00',
            UserType: 'PowerUser',
            CardGuid: 'f2d48a42-895d-41aa-8ad1-1b769b3d7bd2',
            MemberId: '117041388'
          }).toString();
          const response = await fetch(`${API_BASE_URL}/api/Task/status?${params}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!response.ok) {
            throw new Error('Failed to fetch Home Visit Task data');
          }
          const data = await response.json();
          const chartResult = data && Array.isArray(data.chartResult) ? data.chartResult : [];
          setTaskData(chartResult);
          const chartData = processTaskData(chartResult);
          setTaskChartData(chartData);
        } else {
          setTaskData(null);
          setTaskChartData(null);
        }
      } catch (err) {
        setTaskData(null);
        setTaskChartData(null);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialTaskStatus();
    // eslint-disable-next-line
  }, [token]);

  // When date filter changes, fetch from /api/Task/datewise-filter
  useEffect(() => {
    if (!dateFilterActive) return;
    const fetchTaskStatusDatewise = async () => {
      try {
        setLoading(true);
        setError(null);
        if (token) {
          const params = new URLSearchParams({
            CaseManagerOrQueueId: 'patilsmi',
            DueDateFrom: `${taskDateFrom}T00:00:00`,
            DueDateTo: `${taskDateTo}T00:00:00`,
            UserType: 'PowerUser',
            CardGuid: 'f2d48a42-895d-41aa-8ad1-1b769b3d7bd2'
          }).toString();
          const response = await fetch(`${API_BASE_URL}/api/Task/datewise-filter?${params}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!response.ok) {
            throw new Error('Failed to fetch Home Visit Task data');
          }
          const data = await response.json();
          setTaskData(Array.isArray(data) ? data : []);
          const chartData = processTaskData(Array.isArray(data) ? data : []);
          setTaskChartData(chartData);
        } else {
          setTaskData(null);
          setTaskChartData(null);
        }
      } catch (err) {
        setTaskData(null);
        setTaskChartData(null);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTaskStatusDatewise();
    // eslint-disable-next-line
  }, [token, taskDateFrom, taskDateTo, dateFilterActive]);

  // Build chart data to match .NET UserTaskChart model
  const processTaskData = (taskStatusList) => {
    if (!taskStatusList || taskStatusList.length === 0) {
      return null;
    }

    // Build unique labels: CreatedBy (user_id)
    const uniqueLabels = [];
    const labelSet = new Set();
    taskStatusList.forEach(item => {
      if (item.createdBy && item.user_id) {
        const label = `${item.createdBy}(${item.user_id})`;
        if (!labelSet.has(label)) {
          uniqueLabels.push(label);
          labelSet.add(label);
        }
      }
    });

    // For each label, get the statusCount for each status
    const getStatusCount = (status, label) => {
      const found = taskStatusList.find(
        x => x.status === status && `${x.createdBy}(${x.user_id})` === label
      );
      return found ? found.statusCount : 0;
    };

    const completeList = uniqueLabels.map(label => getStatusCount('Complete', label));
    const overdueList = uniqueLabels.map(label => getStatusCount('Over Due', label));
    const futureList = uniqueLabels.map(label => getStatusCount('Future', label));

    return {
      labels: uniqueLabels,
      datasets: [
        {
          label: 'Complete',
          backgroundColor: '#4eaa4e',
          maxBarThickness: 60,
          data: completeList
        },
        {
          label: 'Over Due',
          backgroundColor: '#eb4c44',
          maxBarThickness: 60,
          data: overdueList
        },
        {
          label: 'Future',
          backgroundColor: '#eab00a',
          maxBarThickness: 60,
          data: futureList
        }
      ]
    };
  };

  const handleTaskChartClick = (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const datasetLabel = taskChartData.datasets[element.datasetIndex].label;
      const status = taskChartData.labels[element.index];
      const userId = status.split('(')[1].split(')')[0];
      
      // Navigate to member task details
      const searchParams = new URLSearchParams();
      searchParams.set('userId', userId);
      searchParams.set('status', datasetLabel);
      navigate(`/task/member-details?${searchParams.toString()}`);
    }
  };

  const renderTaskManagement = () => {
    if (loading) {
      return (
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Home Visit Task</h5>
              <div className="text-center">Loading task data...</div>
            </div>
          </div>
        </div>
      );
    }

    // Chart options for embedded display
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      onClick: handleTaskChartClick,
      plugins: {
        legend: {
          display: false // Hide legend since we show it manually above
        },
        title: {
          display: false
        }
      },
      scales: {
        x: {
          maxBarThickness: 60,
          stacked: false,
          grid: {
            display: true,
            color: '#e0e0e0'
          },
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 0
          }
        },
        y: {
          stacked: false,
          beginAtZero: true,
          grid: {
            display: true,
            color: '#e0e0e0'
          },
          ticks: {
            stepSize: 1,
            font: {
              size: 11
            }
          }
        }
      }
    };

    return (
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title" style={{color: '#007bff'}}>Home Visit Task</h5>
            <hr style={{margin: '10px 0'}} />
            {/* Datewise filter controls */}
            <div className="mb-3 d-flex align-items-center" style={{gap: '1rem'}}>
              <label htmlFor="task-date-from" style={{marginBottom: 0}}>From:</label>
              <input
                id="task-date-from"
                type="date"
                value={taskDateFrom}
                onChange={e => {
                  setTaskDateFrom(e.target.value);
                  setDateFilterActive(true);
                }}
                style={{maxWidth: 160}}
              />
              <label htmlFor="task-date-to" style={{marginBottom: 0}}>To:</label>
              <input
                id="task-date-to"
                type="date"
                value={taskDateTo}
                onChange={e => {
                  setTaskDateTo(e.target.value);
                  setDateFilterActive(true);
                }}
                style={{maxWidth: 160}}
              />
            </div>
            {/* Legend */}
            <div className="mb-3 text-center">
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#4eaa4e', marginRight: '5px'}}></span>
                Complete
              </span>
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eb4c44', marginRight: '5px'}}></span>
                Over Due
              </span>
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eab00a', marginRight: '5px'}}></span>
                Future
              </span>
            </div>
            {/* Chart display */}
            <div className="mt-3" style={{ height: '250px' }}>
              {taskChartData ? (
                <Bar ref={chartRef} data={taskChartData} options={chartOptions} />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 border rounded">
                  <div className="text-center text-muted">
                    <div>No task data available</div>
                    <div className="small">Please ensure valid user credentials are provided</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFindHelpCards = () => {
    // Legend for status categories below chart
    const statusLegend = [
      { color: '#4eaa4e', label: 'Closed Loop - Got Help Status', statuses: ['Got Help'] },
      { color: '#eab00a', label: 'In Progress Status', statuses: ['Not Updated', 'Needs Client Action', 'Pending', 'Eligible'] },
      { color: '#eb4c44', label: "Closed Loop - Didn't get Help", statuses: [
        'Referred elsewhere', 'Could not Get Help', 'Could not Contact',
        'Not Eligible', 'No Capacity', 'No Longer Interested'
      ] }
    ];

    // Chart click handler for FindHelp Dashboard
    const handleFindHelpChartClick = async (event, elements) => {
      if (elements.length > 0) {
        const element = elements[0];
        const status = chartData.labels[element.index];
        // Set range to empty string as requested
        const range = '';
        // Build params for API call
        // Get memberID from dashboardModel
        const memberID = dashboardModel.memberID;
        const gridModelParams = new URLSearchParams({
          memberID,
          referralStatus: status,
          range: range
        }).toString();
        try {
          const response = await fetch(`${API_BASE_URL}/api/FindHelp/gridmodel?${gridModelParams}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) throw new Error('Failed to fetch FindHelp grid model');
          const data = await response.json();
          setFindHelpTableData(data);
          setShowFindHelpTable(data.length > 0);
        } catch (err) {
          setFindHelpTableData([]);
          setShowFindHelpTable(false);
        }
      } else {
        setShowFindHelpTable(false);
      }
    };

    // Export table data to Excel
    const handleExportClick = () => {
      if (!findHelpTableData || findHelpTableData.length === 0) {
        alert('No records to download');
        return;
      }
      // Dynamically import SheetJS (xlsx) for client-side export
      import('xlsx').then(XLSX => {
        const ws = XLSX.utils.json_to_sheet(findHelpTableData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ReferralList');
        XLSX.writeFile(wb, 'FindHelpChart.xlsx');
      });
    };
    if (loading) {
      return (
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Find Help Dashboard</h5>
              <div className="text-center">Loading FindHelp data...</div>
            </div>
          </div>
        </div>
      );
    }

    // Only show table if showFindHelpTable is true and findHelpTableData has data

    // Build chart data from combined dashboardModel and pad arrays to match xValues length
    const xValues = dashboardModel && dashboardModel.xValues ? dashboardModel.xValues : [];
    const padArray = (arr) => {
      if (!arr) return Array(xValues.length).fill(0);
      return xValues.map((_, idx) => arr[idx] !== undefined ? arr[idx] : 0);
    };
    // Custom colors for each x value (status)
    const statusColors = [
      '#4eaa4e',    // Got Help
      '#eab00a',    // Not Updated
      '#eab00a',    // Needs Client Action
      '#eab00a',    // Pending
      '#eab00a',    // Eligible
      '#eb4c44',    // Referred elsewhere
      '#eb4c44',    // Could not Get Help
      '#eb4c44',    // Could not Contact
      '#eb4c44',    // Not Eligible
      '#eb4c44',    // No Capacity
      '#eb4c44'     // No Longer Interested
    ];
    // Colors for each dataset (range)
    const rangeColors = [
      '#4eaa4e', // Less than 30 days
      '#eab00a', // 30-45 Days
      '#eb4c44'  // Greater than 45 Days
    ];
    let chartData = {
      labels: xValues,
      datasets: [
        {
          label: 'Less than 30 days',
          backgroundColor: xValues.map(() => rangeColors[0]),
          maxBarThickness: 40,
          data: padArray(dashboardModel && dashboardModel.lessThan30Days)
        },
        {
          label: '30-45 Days',
          backgroundColor: xValues.map(() => rangeColors[1]),
          maxBarThickness: 40,
          data: padArray(dashboardModel && dashboardModel.between30And45Days)
        },
        {
          label: 'Greater than 45 Days',
          backgroundColor: xValues.map(() => rangeColors[2]),
          maxBarThickness: 40,
          data: padArray(dashboardModel && dashboardModel.greaterThan45Days)
        }
      ]
    };

    return (
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title" style={{color: '#007bff'}}>FindHelp Dashboard</h5>
            <hr style={{margin: '10px 0'}} />
            <div className="mb-3 text-center">
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#4eaa4e', marginRight: '5px'}}></span>
                Less than 30 days
              </span>
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eab00a', marginRight: '5px'}}></span>
                30-45 Days
              </span>
              <span className="me-3">
                <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eb4c44', marginRight: '5px'}}></span>
                Greater than 45 Days
              </span>
            </div>
            <div className="mt-3" style={{ height: '300px' }}>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    title: { display: false }
                  },
                  onClick: handleFindHelpChartClick,
                  scales: {
                    x: {
                      grid: { display: true, color: '#e0e0e0' },
                      ticks: {
                        font: { size: 11 },
                        maxRotation: 0,
                        autoSkip: false,
                        color: (context) => {
                          const statusColors = [
                            '#4eaa4e',    // Got Help
                            '#eab00a',    // Not Updated
                            '#eab00a',    // Needs Client Action
                            '#eab00a',    // Pending
                            '#eab00a',    // Eligible
                            '#eb4c44',    // Referred elsewhere
                            '#eb4c44',    // Could not Get Help
                            '#eb4c44',    // Could not Contact
                            '#eb4c44',    // Not Eligible
                            '#eb4c44',    // No Capacity
                            '#eb4c44'     // No Longer Interested
                          ];
                          return statusColors[context.index] || 'black';
                        }
                      }
                    },
                    y: {
                      beginAtZero: true,
                      grid: { display: true, color: '#e0e0e0' },
                      ticks: {
                        stepSize: 1,
                        font: { size: 11 },
                        precision: 0,
                        callback: function(value) {
                          return Number.isInteger(value) ? value : null;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
            {/* Legend below the chart, matching .NET dashboard, with border */}
            <div className="mb-2 d-flex justify-content-center">
              <div style={{
                border: '1px solid #000',
                borderRadius: '6px',
                padding: '8px 0',
                margin: '10px auto',
                width: '60%',
                background: '#fff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {statusLegend.map((item, idx) => (
                  <span key={idx} className="me-3 d-flex align-items-center" style={{minWidth: '200px', justifyContent: 'center'}}>
                    <span style={{
                      display: 'inline-block',
                      width: '18px',
                      height: '18px',
                      backgroundColor: item.color,
                      marginRight: '8px',
                      borderRadius: '3px',
                      border: '1px solid #ceced2'
                    }}></span>
                    <span style={{fontSize: '13px'}}>{item.label}</span>
                  </span>
                ))}
              </div>
            </div>
            {/* Table for FindHelp details on bar click */}
            {showFindHelpTable && findHelpTableData.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  className="btn btn-primary mb-2"
                  style={{ float: 'right', fontSize: 11, fontWeight: 'bold', backgroundColor: '#428BCA' }}
                  onClick={handleExportClick}
                >
                  Export Report
                </button>
                {/* Custom styled table to match .NET dashboard */}
                <style>{`
                  .findhelp-table {
                    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
                    border-collapse: collapse;
                    width: 100%;
                    color: #333;
                    background-color: #fff;
                  }
                  .findhelp-table th, .findhelp-table td {
                    border: 1px solid #ceced2;
                    text-align: center;
                    font-size: 13px;
                  }
                  .findhelp-table thead tr {
                    background-color: #d3d3d5;
                  }
                  .findhelp-table tbody tr:nth-child(odd) {
                    background-color: #f9f9f9;
                  }
                  .findhelp-table tbody tr:nth-child(even) {
                    background-color: #eee;
                  }
                  .findhelp-table tbody tr:hover td {
                    background-color: #b1b1b1;
                    cursor: auto;
                  }
                `}</style>
                <table className="findhelp-table">
                  <thead>
                    <tr>
                      <th>Referral ID</th>
                      <th>Member Name</th>
                      <th>Member ID</th>
                      <th>Medicare</th>
                      <th>Medicaid</th>
                      <th>Staff Name</th>
                      <th>Referral Date</th>
                      <th>Referral Status</th>
                      <th>Referral LastUpdated</th>
                      <th>Provider Name</th>
                      <th>Program Name</th>
                      <th>Category</th>
                      <th>Days Since Referral</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findHelpTableData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.referralID}</td>
                        <td>{item.memberName}</td>
                        <td>{item.memberID}</td>
                        <td>{item.medicare}</td>
                        <td>{item.medicaid}</td>
                        <td>{item.staffName}</td>
                        <td>{item.referralDate}</td>
                        <td>{item.referralStatus}</td>
                        <td>{item.referralLastUpdated}</td>
                        <td>{item.providerName}</td>
                        <td>{item.programName}</td>
                        <td>{item.category}</td>
                        <td>{item.daysSinceReferral}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home">
      <h1>Custom Dashboard</h1>
      <p>Welcome to the Custom Dashboard application.</p>
      <div className="row">
        {renderTaskManagement()}
        {renderFindHelpCards()}
      </div>
    </div>
  );
};

export default Home;




